// KitPwa.ts

import EventManager from "@/Fwk/EventManager";
import DataMgr from "@/Fwk/DataMgr";
import { fwkConst } from "@/Fwk/FwkConst";
import BigTips from "@/Master/BigTips";
import UtilBrowser from "../UtilBrowser";
import EventTrack from "../../Core/EventTrack";
import {fwkEvent} from "@/Fwk/FwkEvent";
import Util from "@/Master/Util";

/**
 * PWA 消息类型
 */
export const PwaMsgType = {
    onChange: "onChange",
    onBeforeInstallPrompt: "onBeforeInstallPrompt",
    onAppInstalled: "onAppInstalled",
    onInstallSuccess: "onInstallSuccess",
    onInstallFail: "onInstallFail",
    onNotAcceptInstall: "onNotAcceptInstall",
    onInitInstalled: "onInitInstalled",
};

type PwaMsgTypeKey = keyof typeof PwaMsgType;

/**
 * PWA 状态枚举
 */
export const PwaStatus = {
    NotInstalled: "NotInstalled",
    InstallPrompt: "InstallPrompt", // 安装提示显示
    Installing: "Installing", // 用户接受安装提示后，PWA 开始安装。这通常是一个短暂的状态，安装过程在后台进行。
    Installed: "Installed", // PWA 安装成功后，应用被添加到用户的主屏幕或应用启动器中。
    InUse: "InUse", // 用户通过主屏幕或应用启动器启动并使用 PWA。
    Update: "Update", // Update Available
    Uninstalled: "Uninstalled", // Uninstalled
};

type PwaStatusKey = keyof typeof PwaStatus;

/**
 * PWA 状态类型
 */
type PwaStatusType = typeof PwaStatus[PwaStatusKey];

/**
 * PWA 消息类型
 */
type PwaMsgTypeValue = typeof PwaMsgType[PwaMsgTypeKey];

/**
 * KitPwa 类，用于管理 PWA 的安装和状态
 */
export default class KitPwa {
    static tag: string = `[KitPwa]`;
    static deferredPrompt: BeforeInstallPromptEvent | null = null;
    static installStatus: boolean = false; // 是否安装成功
    static pwaStatus: PwaStatusType = PwaStatus.NotInstalled; // 当前 PWA 状态
    static locationReloadTime: number | undefined;
    private static isInit: boolean;

    /**
     * 初始化 KitPwa，注册 Service Worker 并设置事件监听器
     */
    static Init(): void {
        if (Util.isDev) return;
        if (this.isInit) return;
        this.isInit = true;

        const _this = this;
        const storageStatus = DataMgr.getStorage(fwkConst.PWA_STATUS) as PwaStatusType | null;
        if (storageStatus) {
            this.pwaStatus = storageStatus;
            this.setPwaStatus(storageStatus);
        }
        _this.UpdatePwaStatus();

        _this.isPWAInstalled().then(isInstalled => {
            if (isInstalled) {
                if (Util.isLog) {
                    console.log(_this.tag, "[experiment] PWA Installed.");
                }
                this.setPwaStatus(PwaStatus.Installed);
                EventManager.dispatchEvent(PwaMsgType.onInitInstalled as PwaMsgTypeValue);
            } else {
                if (Util.isLog) {
                    console.log(_this.tag, "[experiment] PWA Not Installed。");
                }
            }
        });

        _this.registerServiceWorker();

        // 当浏览器检测到 PWA 可以安装时，会触发 beforeinstallprompt 事件
        window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt as EventListener);

        // 当用户成功安装 PWA 后，浏览器会触发 appinstalled 事件，我们可以在此事件中执行一些后续操作。
        window.addEventListener('appinstalled', this.handleAppInstalled as EventListener);

        EventManager.addEventListener(fwkEvent.onAdjustSuccess, () => {
            // 添加您需要在 Adjust 成功后执行的逻辑
        });
    }

    /**
     * 更新 PWA 状态
     */
    static UpdatePwaStatus(): void {
        if (Util.isTestMode) {
            console.log('isInstalled', KitPwa.isInstalled);
        }
        if (KitPwa.isInstalled) {
            KitPwa.setPwaStatus(PwaStatus.Installed);
        } else if (KitPwa.isPlayPwa) {
            KitPwa.setPwaStatus(PwaStatus.InUse);
        } else {
            KitPwa.setPwaStatus(PwaStatus.NotInstalled);
        }

        if (Util.isLog) {
            console.log(this.tag, "UpdatePwaStatus => " + KitPwa.pwaStatus);
            console.log(this.tag, `isInstalled=${KitPwa.isInstalled}, isPlayPwa=${KitPwa.isPlayPwa}, browser PWA installation?=${this.isPWAInstallSupported}`);
        }
    }

    static get isServiceWorker(){
        return 'serviceWorker' in navigator;
    }

    /**
     * 判断当前浏览器是否支持 PWA 安装
     */
    static get isPWAInstallSupported(): boolean {
        return this.isServiceWorker && ('storage' in navigator) && ('getInstalledRelatedApps' in navigator);
        // return ('serviceWorker' in navigator && 'onbeforeinstallprompt' in window);
    }

    /**
     * 注册 Service Worker
     */
    static registerServiceWorker(): void {
        if (this.isServiceWorker) {
            navigator.serviceWorker.register('/sw.js').then(registration => {
                if (Util.isLog) {
                    console.log(this.tag, "[*] ServiceWorker Registration successful, scope: ", registration.scope);
                }
                registration.onupdatefound = () => {
                    const installingWorker = registration.installing;
                    if (Util.isLog) {
                        console.log(this.tag, 'onupdatefound', registration);
                    }
                    if (installingWorker) {
                        installingWorker.onstatechange = () => {
                            if (Util.isLog) {
                                console.log(this.tag, 'onstatechange', installingWorker.state);
                            }
                            if (installingWorker.state === 'installed') {
                                if (navigator.serviceWorker.controller) {
                                    if (KitPwa.isPlayPwa) {
                                        KitPwa.showUpdateMessage(); // 调用函数提示用户刷新页面
                                    }
                                } else {
                                    if (Util.isLog) {
                                        console.log(this.tag, 'Content is cached for offline use。');
                                    }
                                }
                            }
                        };
                    }
                };
            }).catch(error => {
                console.error(this.tag, '[*] Your browser does not support ServiceWorker:', error);
            });
        } else {
            console.log(this.tag, "[*] Your browser does not support ServiceWorker！");
        }
    }

    /**
     * 主动校验更新
     */
    static checkForUpdates(): void {
        if (navigator.serviceWorker) {
            navigator.serviceWorker.getRegistration().then(registration => {
                if (registration) {
                    registration.update();
                }
            });
        }
    }

    /**
     * 处理 'beforeinstallprompt' 事件
     * @param event - BeforeInstallPromptEvent
     */
    static handleBeforeInstallPrompt(event: BeforeInstallPromptEvent): void {
        event.preventDefault();
        KitPwa.deferredPrompt = event;
        KitPwa.setPwaStatus(PwaStatus.InstallPrompt);
        KitPwa.onBeforeInstallPrompt();
    }

    /**
     * 处理 'appinstalled' 事件
     */
    static handleAppInstalled(): void {
        KitPwa.installStatus = true;
        KitPwa.setPwaStatus(PwaStatus.Installed);
        KitPwa.onAppInstalled();
    }

    /**
     * 点击安装 PWA 的处理方法
     */
    static onClickInstallPwa(callResult?: (result: { type: string }) => void): void {
        if (Util.isLog) {
            // console.log(KitPwa.tag,`是否支持PWA:${KitPwa.isPWAInstallSupported},ga=${UtilBrowser.isGoogleChrome},fb=${UtilBrowser.isFacebookApp},name=${UtilBrowser.getBrowserName}`)
            // console.log(KitPwa.tag,`是否支持PWA:${KitPwa.isPWAInstallSupported},ga=${UtilBrowser.isGoogleChrome},fb=${UtilBrowser.isFacebookApp},name=${UtilBrowser.getBrowserName}`);
            // BigTips.showMsg(`是否支持PWA:${KitPwa.isPWAInstallSupported},ga=${UtilBrowser.isGoogleChrome},fb=${UtilBrowser.isFacebookApp},name=${UtilBrowser.getBrowserName}`)
        }
        if (KitPwa.isPWAInstallSupported) {
            KitPwa.installPWA(callResult);
        } else {
            const newUrl = DataMgr.getStorage(fwkConst.NEW_ADJUST_URL) as string | null;

            if (Util.isLog) {
                console.log(KitPwa.tag, `Create a link => ${newUrl}`);
            }

            if (newUrl) {
                UtilBrowser.openInChrome(newUrl);
            } else {
                // if (Util.isLog){
                //     BigTips.showMsg(`没有新的adjust链接`)
                // }
                // Util.logError(`${KitPwa.tag} 没新生成Adjust链接`)
            }
        }
    }

    /**
     * 校验安装 PWA 或者打开 PWA
     */
    static checkInstallPwa(): void {
        if (KitPwa.isPWAInstallSupported) {
            if (KitPwa.isPlayPwa) {
                BigTips.showMsg("Added successfully");
            } else {
                switch (KitPwa.pwaStatus) {
                    case PwaStatus.Installing:
                        return;
                    case PwaStatus.Installed:
                    case PwaStatus.InUse:
                    case PwaStatus.Update:
                        KitPwa.openPWA();
                        break;
                    default:
                        KitPwa.installPWA();
                }
            }
        } else {
            BigTips.showMsg("Please add desktop manually");
        }
    }

    /**
     * 主动触发安装流程的方法
     * @param callResult - 回调函数，用于返回安装结果
     */
    static installPWA(callResult?: (result: { type: string }) => void): void {
        EventTrack.ClickPwaInstall();
        if (KitPwa.deferredPrompt) {
            KitPwa.deferredPrompt.prompt();
            KitPwa.setPwaStatus(PwaStatus.Installing);
            KitPwa.deferredPrompt.userChoice.then(choiceResult => {
                if (choiceResult.outcome === "accepted") {
                    KitPwa.setPwaStatus(PwaStatus.Installing);
                    KitPwa.onInstallSuccess();
                    callResult && callResult({ type: "success" });
                } else {
                    KitPwa.setPwaStatus(PwaStatus.NotInstalled);
                    KitPwa.onNotAcceptInstall();
                }
                KitPwa.deferredPrompt = null; // 清空 deferredPrompt
            });
        } else {
            KitPwa.onInstallFail();
            callResult && callResult({ type: "fail" });
            // Util.logError(`${KitPwa.tag} 安装PWA 失败 => 浏览器是否支持PWA安装=${KitPwa.isPWAInstallSupported}`);
        }
    }

    /**
     * 是否已经安装 PWA
     */
    static get isInstalled(): boolean {
        return KitPwa.installStatus;
    }

    /**
     * 检测当前网页应用是否已经作为 PWA 安装在系统中
     * @returns Promise<boolean> - 是否已安装 PWA
     */
    static async isPWAInstalled(): Promise<boolean> {
        // 检查 navigator.getInstalledRelatedApps 是否可用
        if ('getInstalledRelatedApps' in navigator) {
            return (navigator as any).getInstalledRelatedApps().then((relatedApps: any) => {
                return relatedApps.length > 0;
            })
            .catch((err: any) => {
                return false;
            })
            // return navigator.getInstalledRelatedApps().then(relatedApps) => {
            //     return relatedApps.length > 0;
            // }).catch((error:any) => {
            //     console.error('Error checking installed related apps:', error);
            //     return false;
            // });
        } else {
            return false;
            // return Promise.resolve(false);
        }
    }

    /**
     * 判断当前是否 PWA 环境
     */
    static get isPlayPwa(): boolean {
        return window.matchMedia("(display-mode: standalone)").matches ||
            window.matchMedia("(display-mode: fullscreen)").matches ||
            (window.navigator as any).standalone === true;
    }

    /**
     * 判断当前是否为 iOS 设备上的 PWA
     */
    static get isPlayIOS(): boolean {
        return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    }

    /**
     * 打开 PWA
     */
    static openPWA(): void {
        if (KitPwa.isPlayPwa) {
            window.location.href = './index.html?game=1';
        } else {
            const pwaUrl = '/index.html?game=1'; // PWA 的启动 URL
            window.open(pwaUrl, '_blank');
        }
    }

    /**
     * 注册事件：beforeinstallprompt 事件的处理方法
     */
    static onBeforeInstallPrompt(): void {
        if (Util.isLog) {
            console.log(this.tag, "onBeforeInstallPrompt => can show button");
            // console.log(this.tag, "可以安装PWA回调，这里可以实现其他逻辑，如展示安装按钮");
        }

        EventManager.dispatchEvent(PwaMsgType.onBeforeInstallPrompt as PwaMsgTypeValue);
    }

    /**
     * 注册事件：安装成功的处理方法
     */
    static onAppInstalled(): void {
        if (Util.isLog) {
            console.log(this.tag, "onAppInstalled => can hide bottom");
            // console.log(this.tag, "全局安装成功回调，安装成功的处理方法 这里可以实现其他逻辑，如隐藏安装按钮");
        }

        EventManager.dispatchEvent(PwaMsgType.onAppInstalled as PwaMsgTypeValue);
        // KitPwa.openPWA();
    }

    /**
     * 注册事件：安装成功的处理方法
     */
    static onInstallSuccess(): void {
        if (Util.isLog) {
            console.log(this.tag, "onInstallSuccess => User Trigger");
        }
        EventManager.dispatchEvent(PwaMsgType.onInstallSuccess as PwaMsgTypeValue);
    }

    /**
     * 注册事件：安装失败的处理方法
     */
    static onInstallFail(): void {
        if (Util.isLog) {
            console.log(this.tag, "onInstallFail => User Trigger");
        }
        EventManager.dispatchEvent(PwaMsgType.onInstallFail as PwaMsgTypeValue);
    }

    /**
     * 注册事件：唤起弹窗后未接受安装的处理方法
     */
    static onNotAcceptInstall(): void {
        if (Util.isLog) {
            // console.log(this.tag, "唤起弹窗后未接受安装的处理方法");
            console.log(this.tag, "show install Page => not Install");
        }
        EventManager.dispatchEvent(PwaMsgType.onNotAcceptInstall as PwaMsgTypeValue);
    }

    /**
     * 显示更新提示消息
     */
    static showUpdateMessage(): void {
        if (this.locationReloadTime) {
            clearTimeout(this.locationReloadTime);
        }
        this.locationReloadTime = window.setTimeout(() => {
            const userConfirmed = window.confirm(`New version available, refresh now?  v=`);
            if (userConfirmed) {
                window.location.reload();
            }
        }, 5000); // 延迟5秒刷新
    }

    /**
     * 设置 PWA 状态并更新存储
     * @param status - PWA 状态
     */
    static setPwaStatus(status: PwaStatusType): void {
        KitPwa.pwaStatus = status;
        if (status === PwaStatus.Installed) {
            KitPwa.installStatus = true;
        }
        DataMgr.setStorage(fwkConst.PWA_STATUS, status);

        if (Util.isLog) {
            console.log(this.tag, `set Status => pwaStatus=${KitPwa.pwaStatus}`);
        }

        EventManager.dispatchEvent(PwaMsgType.onChange as PwaMsgTypeValue);
    }
}

/**
 * 扩展 Window 接口以包含 KitPwa
 */
declare global {
    interface Window {
        kitPwa: typeof KitPwa;
    }
}

if (Util.isTestMode && Util.isWindow) {
    window.kitPwa = KitPwa;
}

/**
 * 声明 BeforeInstallPromptEvent 以避免 TypeScript 错误
 */
interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{
        outcome: 'accepted' | 'dismissed';
        platform: string;
    }>;
}
